/**
 * The one Clerk session the gateway transport may use, and the pins that keep a
 * request on the account it started under.
 *
 * Clerk session tokens are only reachable from React, so `AuthBridge` binds the
 * signed-in account here. One Clerk person can hold several accounts (their own
 * and Teams), and Clerk's `getToken` answers for whichever account is active at
 * the moment it is called. A request therefore pins the account subject when it
 * starts, refuses a token minted for any other account, and refuses its own
 * response once the bound account has changed, so nothing from account A is
 * sent with, shown under, or cached under account B.
 */

type TokenGetter = () => Promise<string | null>;

export interface AuthSessionBinding {
  /** The account subject: Clerk user plus active organization (features/auth/account.ts). */
  readonly subject: string;
  /** The active Clerk organization, or null on the person's own account. */
  readonly orgId: string | null;
  readonly sessionId: string;
  readonly getToken: TokenGetter;
}

let authSession: Readonly<AuthSessionBinding> | null = null;

/** Binds one Clerk session and returns an ownership-safe cleanup function. */
export const bindAuthSession = (binding: AuthSessionBinding): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;
  const snapshot = Object.freeze({ ...binding });
  authSession = snapshot;
  return () => {
    if (authSession === snapshot) authSession = null;
  };
};

/** Thrown when the signed-in account changes under a pinned request. */
export class AccountChangedError extends Error {
  constructor() {
    super('The signed-in account changed during this request');
    this.name = 'AccountChangedError';
  }
}

export interface AuthPin {
  /** A token for the pinned account, or null when the pin is unauthenticated. */
  readonly token: TokenGetter;
  /** Throws AccountChangedError unless the pinned account is still the bound one. */
  readonly assertCurrent: () => void;
}

/** The account subject bound right now, or null when signed out. */
export const boundSubject = (): string | null => authSession?.subject ?? null;

/** The organization a Clerk session token was minted for (v2 `o.id`, v1 `org_id`). */
const tokenOrgId = (token: string): string | null => {
  const payload = token.split('.')[1];
  if (!payload) throw new AccountChangedError();
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const claims = JSON.parse(atob(base64)) as { o?: { id?: unknown }; org_id?: unknown };
    const orgId = claims.o?.id ?? claims.org_id;
    return typeof orgId === 'string' && orgId !== '' ? orgId : null;
  } catch {
    throw new AccountChangedError();
  }
};

/**
 * Pins `subject` (default: the account bound right now). A null pin is an
 * unauthenticated request and stays one even if someone signs in meanwhile.
 */
export const pinAuthAccount = (subject: string | null = boundSubject()): AuthPin => {
  const assertCurrent = () => {
    if (boundSubject() !== subject) throw new AccountChangedError();
  };
  const token = async (): Promise<string | null> => {
    if (subject === null) return null;
    const binding = authSession;
    if (binding?.subject !== subject) throw new AccountChangedError();
    const minted = await binding.getToken();
    assertCurrent();
    if (minted !== null && tokenOrgId(minted) !== binding.orgId) throw new AccountChangedError();
    return minted;
  };
  return { token, assertCurrent };
};
